import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type AssistantResultTableJSON = z.infer<typeof AssistantResultTableSchema.AssistantResultTableJSONZ>;
export type AssistantResultTableParams = z.infer<typeof AssistantResultTableSchema.AssistantResultTableZ>;
export type AssistantResultCellParams = z.infer<typeof AssistantResultCellZ>;
export type AssistantResultColumnParams = z.infer<typeof AssistantResultColumnZ>;

const AssistantResultColumnJSONZ = z.object({
  heading: z.string(),
  numeric: z.boolean().nullish(),
});

const AssistantResultCellJSONZ = z.object({
  text: z.string(),
  // Omitted when the cell names something the reader may not be sent to, or that has no page.
  link: z.string().nullish(),
});

const AssistantResultColumnZ = z.object({
  heading: z.string(),
  numeric: z.boolean(),
});

const AssistantResultCellZ = z.object({
  text: z.string(),
  link: z.string().optional(),
});

/**
 * A table a lookup answered with, drawn as a card beneath the assistant's answer (#6223).
 *
 * The figures are the server's, written from what the lookup counted - the answer text refers to
 * the card rather than retyping them. Each cell's link is a route the server chose for a person or
 * record the reader may open; the card still checks it stays inside the application.
 */
export default class AssistantResultTableSchema {
  public static readonly AssistantResultTableJSONZ = z.object({
    title: z.string(),
    summary: z.string().nullish(),
    columns: z.array(AssistantResultColumnJSONZ).min(1),
    rows: z.array(z.array(AssistantResultCellJSONZ)),
    rowsNotShown: z.number().int().nonnegative().nullish(),
    note: z.string().nullish(),
  });

  public static readonly AssistantResultTableZ = z.object({
    title: z.string(),
    summary: z.string().optional(),
    columns: z.array(AssistantResultColumnZ).min(1),
    rows: z.array(z.array(AssistantResultCellZ)),
    rowsNotShown: z.number().int().nonnegative(),
    note: z.string().optional(),
  });

  public static validate(values: unknown): AssistantResultTableParams {
    return parseOrThrow(AssistantResultTableSchema.AssistantResultTableZ, values, {
      logError: true,
      schemaName: "AssistantResultTableSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantResultTableParams {
    try {
      AssistantResultTableSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  /** Params from JSON the caller has already validated, as part of a reply's one parse. */
  public static decodeParsed(json: AssistantResultTableJSON): AssistantResultTableParams {
    return {
      title: json.title,
      summary: json.summary ?? undefined,
      columns: json.columns.map((column) => ({ heading: column.heading, numeric: column.numeric ?? false })),
      rows: json.rows.map((row) => row.map((cell) => ({ text: cell.text, link: cell.link ?? undefined }))),
      rowsNotShown: json.rowsNotShown ?? 0,
      note: json.note ?? undefined,
    };
  }

  public static deserialize(json: AssistantResultTableJSON): AssistantResultTableParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantResultTableSchema.AssistantResultTableJSONZ, json, {
      logError: true,
      schemaName: "AssistantResultTableSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AssistantResultTableSchema.decodeParsed(parsed);
  }

  public static serialize(table: AssistantResultTableParams): AssistantResultTableJSON {
    if (isNullOrUndefined(table)) return table;
    const parsed = AssistantResultTableSchema.validate(table);
    return {
      title: parsed.title,
      summary: parsed.summary,
      columns: parsed.columns,
      rows: parsed.rows,
      rowsNotShown: parsed.rowsNotShown,
      note: parsed.note,
    };
  }
}
